import React, { useState } from 'react';
import Icon from '../AppIcon';

const Sidebar = ({ isCollapsed = false, onToggle }) => {
  const [expandedSections, setExpandedSections] = useState({
    intelligence: false
  });

  const navigationItems = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      path: '/dashboard',
      icon: 'LayoutDashboard',
      description: 'Portfolio overview and activity monitoring'
    },
    {
      id: 'portfolio',
      label: 'Credit Portfolio',
      path: '/credit-portfolio',
      icon: 'Briefcase',
      description: 'Manage and verify carbon credits'
    },
    {
      id: 'claims',
      label: 'Claims Builder',
      path: '/claims-builder',
      icon: 'FileText',
      description: 'VCMI-compliant retirement claims'
    },
    {
      id: 'compliance',
      label: 'Article 6 Gateway',
      path: '/article-6-gateway',
      icon: 'Shield',
      description: 'International regulatory compliance'
    },
    {
      id: 'intelligence',
      label: 'Intelligence Hub',
      icon: 'Brain',
      description: 'Analytical tools and insights',
      isExpandable: true,
      children: [
        {
          id: 'methodology',
          label: 'Methodology Intelligence',
          path: '/methodology-intelligence',
          icon: 'BookOpen',
          description: 'Research and methodology analysis'
        },
        {
          id: 'provenance',
          label: 'Provenance Tracking',
          path: '/provenance-tracking',
          icon: 'GitBranch',
          description: 'Credit origin and chain verification'
        },
        {
          id: 'registry',
          label: 'Registry Connectors',
          path: '/registry-connectors',
          icon: 'Link',
          description: 'Registry monitoring and connectivity'
        }
      ]
    }
  ];

  const currentPath = window.location?.pathname;

  const handleNavigation = (path) => {
    if (path) {
      window.location.href = path;
    }
  };

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev?.[sectionId]
    }));
  };

  const isActiveItem = (item) => {
    if (item?.path) {
      return currentPath === item?.path;
    }
    if (item?.children) {
      return item?.children?.some(child => currentPath === child?.path);
    }
    return false;
  };

  return (
    <aside className={`fixed left-0 top-16 bottom-0 z-100 bg-card border-r border-border transition-all duration-300 ${
      isCollapsed ? 'w-16' : 'w-64'
    }`}>
      <div className="flex flex-col h-full">
        {/* Toggle Button */}
        <div className="flex items-center justify-between p-4 border-b border-border">
          {!isCollapsed && (
            <span className="text-sm font-medium text-muted-foreground">Navigation</span>
          )}
          <button
            onClick={onToggle}
            className="p-1 hover:bg-muted rounded-lg transition-smooth"
          >
            <Icon 
              name={isCollapsed ? 'ChevronRight' : 'ChevronLeft'} 
              size={16} 
            />
          </button>
        </div>

        {/* Navigation Items */}
        <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
          {navigationItems?.map((item) => (
            <div key={item?.id}>
              {/* Main Item */}
              <button
                onClick={() => {
                  if (item?.isExpandable) {
                    toggleSection(item?.id);
                  } else {
                    handleNavigation(item?.path);
                  }
                }}
                className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-left transition-smooth group ${
                  isActiveItem(item)
                    ? 'bg-primary text-primary-foreground'
                    : 'text-card-foreground hover:bg-muted'
                }`}
                title={isCollapsed ? item?.label : ''}
              >
                <Icon 
                  name={item?.icon} 
                  size={20} 
                  className="flex-shrink-0"
                />
                {!isCollapsed && (
                  <>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{item?.label}</div>
                      <div className="text-xs opacity-70 truncate">{item?.description}</div>
                    </div>
                    {item?.isExpandable && (
                      <Icon 
                        name={expandedSections?.[item?.id] ? 'ChevronDown' : 'ChevronRight'} 
                        size={16} 
                        className="flex-shrink-0"
                      />
                    )}
                  </>
                )}
              </button>

              {/* Expandable Children */}
              {item?.isExpandable && !isCollapsed && expandedSections?.[item?.id] && (
                <div className="ml-6 mt-2 space-y-1 border-l border-border pl-4">
                  {item?.children?.map((child) => (
                    <button
                      key={child?.id}
                      onClick={() => handleNavigation(child?.path)}
                      className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-left transition-smooth ${
                        currentPath === child?.path
                          ? 'bg-accent text-accent-foreground'
                          : 'text-card-foreground hover:bg-muted'
                      }`}
                    >
                      <Icon 
                        name={child?.icon} 
                        size={16} 
                        className="flex-shrink-0"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{child?.label}</div>
                        <div className="text-xs opacity-70 truncate">{child?.description}</div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </nav>

        {/* Status Section */}
        <div className="p-4 border-t border-border">
          <div className={`flex items-center space-x-3 px-3 py-2 bg-success/10 rounded-lg ${
            isCollapsed ? 'justify-center' : ''
          }`}>
            <div className="w-2 h-2 bg-success rounded-full animate-pulse flex-shrink-0"></div>
            {!isCollapsed && (
              <div className="flex-1 min-w-0">
                <div className="text-xs font-medium text-success">System Status</div>
                <div className="text-xs text-success/80">All registries connected</div>
              </div>
            )}
          </div>
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;